import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FrontHeader from '../components/common/FrontHeader';
import FrontFooter from '../components/common/FrontFooter';
import './AvailableJobsPublic.css';

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:5000/api'
    : '/api')
).replace(/\/+$/, '');

const SAVED_KEY = 'seazex_public_saved_jobs';

const DEFAULT_FILTERS = {
  search: '',
  port: '',
  service: '',
  budgetType: '',
  deadline: '',
  sort: 'newest',
  savedOnly: false
};

const asText = value =>
  typeof value === 'string' ? value.trim() : '';

const getTime = value => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

const getNumber = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number) && number >= 0
    ? number
    : null;
};

const formatDate = value => {
  const time = getTime(value);

  return time === null
    ? 'Not specified'
    : new Intl.DateTimeFormat('en', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).format(time);
};

const formatMoney = (value, currency) => {
  const amount = getNumber(value);

  if (amount === null) return '';

  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString('en')}`;
  }
};

const budgetTypeLabel = type => ({
  fixed: 'Fixed price',
  hourly: 'Hourly',
  quotation: 'Quotation requested'
}[type] || 'Not specified');

const budgetLabel = job => {
  if (job.budgetType === 'quotation') {
    return 'Request for quotation';
  }

  const currency = job.currency || 'USD';
  const amount = getNumber(job.budget);
  const min = getNumber(job.budgetMin);
  const max = getNumber(job.budgetMax);

  if (job.budgetType === 'hourly') {
    return amount === null
      ? 'Hourly rate negotiable'
      : `${formatMoney(amount, currency)} / hr`;
  }

  if (min !== null && max !== null) {
    return `${formatMoney(min, currency)} – ${formatMoney(max, currency)}`;
  }

  if (amount !== null) return formatMoney(amount, currency);
  if (min !== null) return `From ${formatMoney(min, currency)}`;
  if (max !== null) return `Up to ${formatMoney(max, currency)}`;

  return 'Budget not specified';
};

const postedLabel = (value, now) => {
  const time = getTime(value);

  if (time === null) return 'Date unavailable';

  const minutes = Math.max(0, Math.floor((now - time) / 60000));

  if (minutes < 1) return 'Just posted';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);

  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);

  return days < 30 ? `${days}d ago` : formatDate(value);
};

const deadlineInfo = (value, now) => {
  const time = getTime(value);

  if (time === null) {
    return { label: 'No deadline', urgent: false };
  }

  const remaining = time - now;

  if (remaining <= 0) {
    return { label: 'Deadline passed', urgent: false };
  }

  if (remaining <= 86400000) {
    return { label: 'Closes within 24h', urgent: true };
  }

  const days = Math.ceil(remaining / 86400000);

  return {
    label: `${days} days left`,
    urgent: days <= 3
  };
};

const readSavedJobs = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(SAVED_KEY));

    return Array.isArray(stored)
      ? [...new Set(stored.filter(id => typeof id === 'string'))]
      : [];
  } catch {
    return [];
  }
};

const JobCard = ({ job, user, authLoading, now, saved, onSave }) => {
  const [descriptionOpen, setDescriptionOpen] = useState(false);

  const description = asText(job.description);
  const longDescription = description.length > 230;
  const deadline = deadlineInfo(job.deadline, now);
  const grt = getNumber(job.grt);

  const company =
    asText(job.ownerId?.company) ||
    asText(job.ownerId?.name) ||
    'Ship owner';

  const attachmentCount =
    getNumber(job.attachmentCount) ??
    (Array.isArray(job.attachments) ? job.attachments.length : 0);

  const descriptionId = `ajp-description-${job._id}`;

  return (
    <article className="ajp-card">
      <div className="ajp-card-top">
        <div className="ajp-badges">
          <span className="ajp-badge">
            {budgetTypeLabel(job.budgetType)}
          </span>

          <span
            className={`ajp-deadline ${
              deadline.urgent ? 'is-urgent' : ''
            }`}
            title={formatDate(job.deadline)}
          >
            <i className="far fa-clock" aria-hidden="true" />
            {deadline.label}
          </span>
        </div>

        <button
          type="button"
          className={`ajp-save ${saved ? 'is-saved' : ''}`}
          aria-pressed={saved}
          aria-label={saved ? 'Remove saved job' : 'Save job'}
          title={saved ? 'Remove saved job' : 'Save on this browser'}
          onClick={() => onSave(String(job._id))}
        >
          <i
            className={saved ? 'fas fa-bookmark' : 'far fa-bookmark'}
            aria-hidden="true"
          />
        </button>
      </div>

      <div className="ajp-title-row">
        <div className="ajp-title-content">
          <h2 className="ajp-job-title">
            <Link to={`/jobs/${job._id}`}>
              {asText(job.serviceType) || 'Maritime service request'}
            </Link>
          </h2>

          <div className="ajp-job-meta">
            <span>
              <i className="fas fa-ship" aria-hidden="true" />
              {asText(job.vesselName) || 'Vessel not specified'}
            </span>

            <span>
              <i className="fas fa-map-marker-alt" aria-hidden="true" />
              {asText(job.port) || 'Port not specified'}
            </span>
          </div>
        </div>

        <div className="ajp-budget">
          <span>Budget</span>
          <strong>{budgetLabel(job)}</strong>
        </div>
      </div>

      <div className="ajp-description-block">
        <p className="ajp-description" id={descriptionId}>
          {description
            ? longDescription && !descriptionOpen
              ? `${description.slice(0, 230).trimEnd()}…`
              : description
            : 'No description provided.'}
        </p>

        {longDescription && (
          <button
            type="button"
            className="ajp-text-button"
            aria-expanded={descriptionOpen}
            aria-controls={descriptionId}
            onClick={() => setDescriptionOpen(value => !value)}
          >
            {descriptionOpen ? 'Show less' : 'Read more'}
            <i
              className={`fas fa-chevron-${descriptionOpen ? 'up' : 'down'}`}
              aria-hidden="true"
            />
          </button>
        )}
      </div>

      <div className="ajp-facts">
        <div>
          <span>Deadline</span>
          <strong>{formatDate(job.deadline)}</strong>
        </div>

        <div>
          <span>Next port</span>
          <strong>{asText(job.nextPort) || 'Not specified'}</strong>
        </div>

        <div>
          <span>Class approval</span>
          <strong>{asText(job.classApproval) || 'Not specified'}</strong>
        </div>

        <div>
          <span>Documents</span>
          <strong>
            {attachmentCount ? `${attachmentCount} attached` : 'None'}
          </strong>
        </div>
      </div>

      <details className="ajp-details">
        <summary>
          <span>
            <i className="fas fa-sliders-h" aria-hidden="true" />
            Additional information
          </span>
          <i className="fas fa-chevron-down" aria-hidden="true" />
        </summary>

        <div className="ajp-details-body">
          <dl className="ajp-detail-grid">
            <div>
              <dt>Posted by</dt>
              <dd>{company}</dd>
            </div>

            <div>
              <dt>Gross tonnage</dt>
              <dd>
                {grt === null ? 'Not specified' : grt.toLocaleString('en')}
              </dd>
            </div>

            <div>
              <dt>Budget type</dt>
              <dd>{budgetTypeLabel(job.budgetType)}</dd>
            </div>

            <div>
              <dt>Currency</dt>
              <dd>{job.currency || 'USD'}</dd>
            </div>

            <div>
              <dt>Published</dt>
              <dd>{formatDate(job.createdAt)}</dd>
            </div>

            <div>
              <dt>Last updated</dt>
              <dd>{formatDate(job.updatedAt || job.createdAt)}</dd>
            </div>
          </dl>

          <div className="ajp-reference">
            <span>Reference</span>
            <code>{job._id}</code>
          </div>

          {attachmentCount > 0 && (
            <p className="ajp-document-note">
              Supporting documents are accessed through the service-provider job page.
            </p>
          )}
        </div>
      </details>

      <div className="ajp-card-footer">
        <div className="ajp-posted">
          <span>{company}</span>
          <time title={formatDate(job.createdAt)}>
            {postedLabel(job.createdAt, now)}
          </time>
        </div>

        <div className="ajp-card-actions">
          <Link
            to={`/jobs/${job._id}`}
            className="ajp-button ajp-button-secondary"
          >
            View details
            <i className="fas fa-arrow-right" aria-hidden="true" />
          </Link>

          {authLoading ? null : user?.role === 'service-provider' ? (
            <Link
              to={`/service-provider/submit-quotation/${job._id}`}
              className="ajp-button ajp-button-primary"
            >
              Submit quotation
            </Link>
          ) : !user ? (
            <Link
              to="/register"
              className="ajp-button ajp-button-primary"
            >
              Register to quote
            </Link>
          ) : null}
        </div>
      </div>
    </article>
  );
};

const AvailableJobsPublic = () => {
  const { user, loading: authLoading } = useAuth();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [savedIds, setSavedIds] = useState(readSavedJobs);
  const [storageNotice, setStorageNotice] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(4);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [now, setNow] = useState(Date.now);
  const [updatedAt, setUpdatedAt] = useState(null);

  const controllerRef = useRef(null);
  const resultsRef = useRef(null);

  const fetchJobs = useCallback(async () => {
    controllerRef.current?.abort();

    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/public/rfqs`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
        cache: 'no-store'
      });

      const data = await response.json().catch(() => null);

      if (
        !response.ok ||
        data?.success !== true ||
        !Array.isArray(data.rfqs)
      ) {
        throw new Error('Unable to load jobs.');
      }

      if (controller.signal.aborted) return;

      setJobs(
        data.rfqs.filter(job =>
          job &&
          job._id &&
          job.status === 'open' &&
          job.isPrivate !== true
        )
      );

      setNow(Date.now());
      setUpdatedAt(new Date());
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(
          'Unable to refresh jobs. Check your connection and try again.'
        );
      }
    } finally {
      if (
        controllerRef.current === controller &&
        !controller.signal.aborted
      ) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    return () => controllerRef.current?.abort();
  }, [fetchJobs]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SAVED_KEY, JSON.stringify(savedIds));
      setStorageNotice('');
    } catch {
      setStorageNotice(
        'Browser storage is unavailable. Saved jobs will last only for this visit.'
      );
    }
  }, [savedIds]);

  const openJobs = useMemo(
    () => jobs.filter(job => {
      const deadline = getTime(job.deadline);
      return deadline !== null && deadline > now;
    }),
    [jobs, now]
  );

  const ports = useMemo(
    () => [...new Set(
      openJobs.map(job => asText(job.port)).filter(Boolean)
    )].sort((a, b) => a.localeCompare(b)),
    [openJobs]
  );

  const services = useMemo(
    () => [...new Set(
      openJobs.map(job => asText(job.serviceType)).filter(Boolean)
    )].sort((a, b) => a.localeCompare(b)),
    [openJobs]
  );

  const savedSet = useMemo(() => new Set(savedIds), [savedIds]);

  const savedCount = openJobs.filter(job =>
    savedSet.has(String(job._id))
  ).length;

  const filteredJobs = useMemo(() => {
    const words = filters.search
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    const result = openJobs.filter(job => {
      const searchableText = [
        job.serviceType,
        job.vesselName,
        job.port,
        job.nextPort,
        job.description,
        job.classApproval,
        job.ownerId?.company,
        job.ownerId?.name
      ].map(asText).join(' ').toLowerCase();

      const deadline = getTime(job.deadline);

      return (
        words.every(word => searchableText.includes(word)) &&
        (!filters.port || asText(job.port) === filters.port) &&
        (!filters.service || asText(job.serviceType) === filters.service) &&
        (!filters.budgetType || job.budgetType === filters.budgetType) &&
        (!filters.savedOnly || savedSet.has(String(job._id))) &&
        (
          !filters.deadline ||
          (
            deadline !== null &&
            deadline > now &&
            deadline <= now + Number(filters.deadline) * 86400000
          )
        )
      );
    });

    return result.sort((a, b) => {
      if (filters.sort === 'deadline') {
        return (
          (getTime(a.deadline) ?? Infinity) -
          (getTime(b.deadline) ?? Infinity)
        );
      }

      if (filters.sort === 'oldest') {
        return (
          (getTime(a.createdAt) ?? 0) -
          (getTime(b.createdAt) ?? 0)
        );
      }

      return (
        (getTime(b.createdAt) ?? 0) -
        (getTime(a.createdAt) ?? 0)
      );
    });
  }, [openJobs, filters, savedSet, now]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredJobs.length / pageSize)
  );

  const page = Math.min(currentPage, totalPages);
  const start = (page - 1) * pageSize;
  const visibleJobs = filteredJobs.slice(start, start + pageSize);

  const activeFilterCount = [
    filters.port,
    filters.service,
    filters.budgetType,
    filters.deadline,
    filters.savedOnly
  ].filter(Boolean).length;

  const hasFilters =
    activeFilterCount > 0 || Boolean(filters.search.trim());

  const updateFilter = (key, value) => {
    setFilters(previous => ({ ...previous, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setCurrentPage(1);
  };

  const toggleSave = id => {
    setSavedIds(previous =>
      previous.includes(id)
        ? previous.filter(savedId => savedId !== id)
        : [...previous, id]
    );
  };

  const changePage = nextPage => {
    setCurrentPage(Math.max(1, Math.min(totalPages, nextPage)));

    requestAnimationFrame(() => {
      resultsRef.current?.focus({ preventScroll: true });
      resultsRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'start'
      });
    });
  };

  return (
    <div className="ajp-page">
      <FrontHeader />

      <main className="ajp-container">
        <header className="ajp-hero">
          <div>
            <span className="ajp-eyebrow">
              <span className="ajp-dot" />
              MARITIME OPPORTUNITIES
            </span>

            <h1>
              Available <span>jobs.</span>
            </h1>

            <p>
              Find service requests that match your expertise and operating ports.
            </p>
          </div>

          <div className="ajp-hero-stats">
            <div>
              <strong>{loading && !updatedAt ? '—' : openJobs.length}</strong>
              <span>Open jobs</span>
            </div>

            <div>
              <strong>{loading && !updatedAt ? '—' : ports.length}</strong>
              <span>Ports</span>
            </div>

            <div>
              <strong>{loading && !updatedAt ? '—' : services.length}</strong>
              <span>Services</span>
            </div>
          </div>
        </header>

        <div className="ajp-layout">
          <aside className="ajp-sidebar" aria-label="Job filters">
            <div className="ajp-sidebar-heading">
              <h2>
                <i className="fas fa-sliders-h" aria-hidden="true" />
                Filters
                {activeFilterCount > 0 && (
                  <span>{activeFilterCount}</span>
                )}
              </h2>

              <button
                type="button"
                className="ajp-text-button ajp-desktop-reset"
                onClick={resetFilters}
                disabled={!hasFilters}
              >
                Reset
              </button>

              <button
                type="button"
                className="ajp-mobile-toggle"
                aria-controls="ajp-filter-body"
                aria-expanded={mobileFiltersOpen}
                onClick={() => setMobileFiltersOpen(value => !value)}
              >
                {mobileFiltersOpen ? 'Hide' : 'Show'}
                <i
                  className={`fas fa-chevron-${mobileFiltersOpen ? 'up' : 'down'}`}
                  aria-hidden="true"
                />
              </button>
            </div>

            <div
              id="ajp-filter-body"
              className={`ajp-filter-body ${mobileFiltersOpen ? 'is-open' : ''}`}
            >
              <div className="ajp-filter-fields">
                <label className="ajp-control">
                  <span>Port of operation</span>
                  <select
                    value={filters.port}
                    onChange={event => updateFilter('port', event.target.value)}
                  >
                    <option value="">All ports</option>
                    {ports.map(port => (
                      <option key={port} value={port}>{port}</option>
                    ))}
                  </select>
                </label>

                <label className="ajp-control">
                  <span>Service category</span>
                  <select
                    value={filters.service}
                    onChange={event => updateFilter('service', event.target.value)}
                  >
                    <option value="">All services</option>
                    {services.map(service => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </label>

                <label className="ajp-control">
                  <span>Budget type</span>
                  <select
                    value={filters.budgetType}
                    onChange={event => updateFilter('budgetType', event.target.value)}
                  >
                    <option value="">All budget types</option>
                    <option value="fixed">Fixed price</option>
                    <option value="hourly">Hourly</option>
                    <option value="quotation">Quotation requested</option>
                  </select>
                </label>

                <label className="ajp-control">
                  <span>Submission deadline</span>
                  <select
                    value={filters.deadline}
                    onChange={event => updateFilter('deadline', event.target.value)}
                  >
                    <option value="">Any deadline</option>
                    <option value="3">Within 3 days</option>
                    <option value="7">Within 7 days</option>
                    <option value="30">Within 30 days</option>
                  </select>
                </label>
              </div>

              <button
                type="button"
                className={`ajp-saved-filter ${filters.savedOnly ? 'is-active' : ''}`}
                aria-pressed={filters.savedOnly}
                onClick={() => updateFilter('savedOnly', !filters.savedOnly)}
              >
                <span>
                  <i className="far fa-bookmark" aria-hidden="true" />
                  Saved jobs only
                </span>
                <strong>{savedCount}</strong>
              </button>

              <div className="ajp-sidebar-summary">
                <div>
                  <span>Matching jobs</span>
                  <strong>{filteredJobs.length}</strong>
                </div>

                <div>
                  <span>Available ports</span>
                  <strong>{ports.length}</strong>
                </div>

                <div>
                  <span>Service categories</span>
                  <strong>{services.length}</strong>
                </div>
              </div>

              <button
                type="button"
                className="ajp-button ajp-button-secondary ajp-reset-all"
                onClick={resetFilters}
                disabled={!hasFilters}
              >
                Clear all filters
              </button>

              <p className="ajp-sidebar-note">
                <i className="fas fa-info-circle" aria-hidden="true" />
                Saved jobs stay on this browser.
              </p>
            </div>
          </aside>

          <section
            className="ajp-results"
            ref={resultsRef}
            tabIndex={-1}
            aria-label="Available job results"
            aria-busy={loading}
          >
            <div className="ajp-search-row">
              <div className="ajp-search">
                <i className="fas fa-search" aria-hidden="true" />

                <input
                  type="search"
                  value={filters.search}
                  onChange={event => updateFilter('search', event.target.value)}
                  placeholder="Search service, vessel, port or keyword..."
                  aria-label="Search available jobs"
                />

                {filters.search && (
                  <button
                    type="button"
                    className="ajp-clear-search"
                    onClick={() => updateFilter('search', '')}
                    aria-label="Clear search"
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                )}
              </div>

              <button
                type="button"
                className="ajp-button ajp-button-secondary"
                onClick={fetchJobs}
                disabled={loading}
                aria-label="Refresh jobs"
              >
                <i
                  className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}
                  aria-hidden="true"
                />
                <span className="ajp-refresh-label">Refresh</span>
              </button>
            </div>

            <div className="ajp-results-toolbar">
              <div className="ajp-results-count" role="status">
                <strong>{filteredJobs.length}</strong>
                {' '}matching {filteredJobs.length === 1 ? 'job' : 'jobs'}
              </div>

              <div className="ajp-result-controls">
                <label>
                  <span>Sort</span>
                  <select
                    value={filters.sort}
                    onChange={event => updateFilter('sort', event.target.value)}
                  >
                    <option value="newest">Newest first</option>
                    <option value="deadline">Closing soon</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                </label>

                <label>
                  <span>Show</span>
                  <select
                    value={pageSize}
                    onChange={event => {
                      setPageSize(Number(event.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={4}>4</option>
                    <option value={8}>8</option>
                    <option value={12}>12</option>
                  </select>
                </label>
              </div>
            </div>

            {storageNotice && (
              <p className="ajp-notice" role="status">
                {storageNotice}
              </p>
            )}

            {error && (
              <div className="ajp-error" role="alert">
                <span>
                  {error}
                  {jobs.length > 0 && ' Previously loaded jobs are shown below.'}
                </span>

                <button
                  type="button"
                  className="ajp-text-button"
                  onClick={fetchJobs}
                  disabled={loading}
                >
                  Try again
                </button>
              </div>
            )}

            {loading && jobs.length === 0 ? (
              <div className="ajp-empty" role="status">
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                <h2>Loading opportunities</h2>
                <p>Fetching the latest public service requests.</p>
              </div>
            ) : error && jobs.length === 0 ? null : visibleJobs.length === 0 ? (
              <div className="ajp-empty">
                <i
                  className={filters.savedOnly ? 'far fa-bookmark' : 'fas fa-search'}
                  aria-hidden="true"
                />

                <h2>
                  {filters.savedOnly ? 'No matching saved jobs' : 'No matching jobs'}
                </h2>

                <p>
                  {filters.savedOnly
                    ? 'Save jobs using the bookmark button, or adjust your filters.'
                    : 'Try another keyword, port or service category.'}
                </p>

                {hasFilters && (
                  <button
                    type="button"
                    className="ajp-button ajp-button-secondary"
                    onClick={resetFilters}
                  >
                    Reset filters
                  </button>
                )}
              </div>
            ) : (
              <div className="ajp-job-list">
                {visibleJobs.map(job => (
                  <JobCard
                    key={job._id}
                    job={job}
                    user={user}
                    authLoading={authLoading}
                    now={now}
                    saved={savedSet.has(String(job._id))}
                    onSave={toggleSave}
                  />
                ))}
              </div>
            )}

            {filteredJobs.length > 0 && (
              <nav className="ajp-pagination" aria-label="Job pagination">
                <span>
                  Showing {start + 1}–
                  {Math.min(start + pageSize, filteredJobs.length)}
                  {' '}of {filteredJobs.length}
                </span>

                <div>
                  <button
                    type="button"
                    className="ajp-button ajp-button-secondary"
                    onClick={() => changePage(page - 1)}
                    disabled={page === 1}
                    aria-label="Previous page"
                  >
                    <i className="fas fa-chevron-left" aria-hidden="true" />
                  </button>

                  <span aria-live="polite">
                    {page} / {totalPages}
                  </span>

                  <button
                    type="button"
                    className="ajp-button ajp-button-secondary"
                    onClick={() => changePage(page + 1)}
                    disabled={page === totalPages}
                    aria-label="Next page"
                  >
                    <i className="fas fa-chevron-right" aria-hidden="true" />
                  </button>
                </div>
              </nav>
            )}

            <div className="ajp-bottom-note">
              {updatedAt && (
                <span>
                  Updated {updatedAt.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              )}

              {!user && !authLoading && (
                <Link to="/login">
                  Already registered? Sign in
                </Link>
              )}
            </div>
          </section>
        </div>
      </main>

      <FrontFooter />
    </div>
  );
};

export default AvailableJobsPublic;